import React, { useCallback, useEffect, useState } from "react";
import { API_URL } from "../../config";

function GestionTestimonios() {
  const [items, setItems] = useState([]);
  const [author, setAuthor] = useState("");
  const [text, setText] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [mensaje, setMensaje] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const loadTestimonials = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/testimonios?global=true&limit=100`, {
        credentials: "include",
      });
      if (!res.ok) {
        setError(`No se pudieron cargar los testimonios (${res.status}).`);
        return;
      }
      const data = await res.json();
      const list = Array.isArray(data?.items) ? data.items : [];
      setItems(list);
      setSelectedIds((prev) => prev.filter((id) => list.some((item) => item._id === id)));
    } catch {
      setError("No se pudieron cargar los testimonios.");
    }
  }, []);

  useEffect(() => {
    loadTestimonials();
  }, [loadTestimonials]);

  const toggleSelected = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === items.length) {
      setSelectedIds([]);
      return;
    }
    setSelectedIds(items.map((item) => item._id));
  };

  const handleCreate = async (event) => {
    event.preventDefault();
    if (!author.trim() || !text.trim()) {
      setError("Autor y texto son obligatorios.");
      return;
    }
    setSaving(true);
    setMensaje("");
    setError("");
    try {
      const res = await fetch(`${API_URL}/api/testimonios`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ author: author.trim(), text: text.trim() }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || "No se pudo crear el testimonio.");
        return;
      }
      setAuthor("");
      setText("");
      setMensaje("Testimonio creado correctamente.");
      await loadTestimonials();
    } catch {
      setError("Error de conexión al crear el testimonio.");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedIds.length === 0) {
      setError("Selecciona al menos un testimonio para eliminar.");
      return;
    }
    const confirmed = globalThis.confirm(
      `¿Eliminar ${selectedIds.length} testimonio(s) seleccionado(s)? Esta acción no se puede deshacer.`,
    );
    if (!confirmed) {
      return;
    }
    setDeleting(true);
    setMensaje("");
    setError("");
    try {
      const results = await Promise.all(
        selectedIds.map(async (id) => {
          const res = await fetch(`${API_URL}/api/testimonios/${id}`, {
            method: "DELETE",
            credentials: "include",
          });
          return { id, ok: res.ok };
        }),
      );
      const deletedCount = results.filter((r) => r.ok).length;
      if (deletedCount === 0) {
        setError("No se pudo eliminar ningún testimonio.");
        return;
      }
      setMensaje(`${deletedCount} testimonio(s) eliminado(s).`);
      setSelectedIds([]);
      await loadTestimonials();
    } catch {
      setError("Error de conexión al eliminar testimonios.");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <section className="cfgx-section">
      <h2>Testimonios de la página pública</h2>
      <p className="cfgx-help">
        Gestiona los testimonios globales que aparecen solo en la página principal.
      </p>

      <form className="cfgx-testimonial-form" onSubmit={handleCreate}>
        <div className="cfgx-field">
          <label htmlFor="cfg-testimonial-author">Autor</label>
          <input
            id="cfg-testimonial-author"
            type="text"
            className="cfgx-input"
            value={author}
            onChange={(e) => setAuthor(e.target.value)}
            maxLength={100}
          />
        </div>
        <div className="cfgx-field">
          <label htmlFor="cfg-testimonial-text">Texto</label>
          <textarea
            id="cfg-testimonial-text"
            className="cfgx-textarea"
            rows={4}
            value={text}
            onChange={(e) => setText(e.target.value)}
            maxLength={2000}
          />
        </div>
        <button type="submit" className="cfgx-btn-guardar" disabled={saving}>
          {saving ? "Guardando…" : "Añadir testimonio"}
        </button>
      </form>

      <div className="cfgx-list-actions">
        <label className="cfgx-checkbox-label">
          <input
            type="checkbox"
            checked={items.length > 0 && selectedIds.length === items.length}
            onChange={toggleSelectAll}
            disabled={items.length === 0}
          />
          Seleccionar todos
        </label>
        <button
          type="button"
          className="cfgx-btn-quitar"
          onClick={handleDeleteSelected}
          disabled={deleting || selectedIds.length === 0}
        >
          {deleting ? "Eliminando…" : `Eliminar seleccionados (${selectedIds.length})`}
        </button>
      </div>

      {items.length === 0 ? (
        <p className="cfgx-help">No hay testimonios globales todavía.</p>
      ) : (
        <ul className="cfgx-testimonial-list">
          {items.map((item) => (
            <li key={item._id} className="cfgx-testimonial-item">
              <label className="cfgx-checkbox-label">
                <input
                  type="checkbox"
                  checked={selectedIds.includes(item._id)}
                  onChange={() => toggleSelected(item._id)}
                />
                <span>
                  <strong>{item.author}</strong>
                  <p>{item.text}</p>
                </span>
              </label>
            </li>
          ))}
        </ul>
      )}

      {error ? <p className="cfgx-error">{error}</p> : null}
      {mensaje ? <p className="cfgx-msg">{mensaje}</p> : null}
    </section>
  );
}

export default GestionTestimonios;
